import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { alpha, keyframes } from "@mui/material/styles";
import LaunchIcon from "@mui/icons-material/Launch";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EmailIcon from "@mui/icons-material/Email";
import ApiIcon from "@mui/icons-material/Api";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const platforms = [
  {
    name: "Fiverr",
    icon: <StorefrontIcon />,
    color: "#1DBF73",
    profileUrl: "https://www.fiverr.com/rahmanidrees",
    description:
      "Use Fiverr for clearly scoped MERN, dashboard, integration, and Web3 implementation tasks.",
    highlights: ["Fixed scope", "Milestones", "Fast project start"],
  },
  {
    name: "Upwork",
    icon: <BusinessCenterIcon />,
    color: "#14A800",
    profileUrl: "https://www.upwork.com/freelancers/~01aa410a82227bed51?mp_source=share",
    description:
      "Use Upwork for longer SaaS builds, maintenance work, integrations, and ongoing product development.",
    highlights: ["Hourly or fixed", "Long-term work", "Product support"],
  },
];

const consultationOptions = [
  {
    title: "Project Consultation",
    icon: <HandshakeIcon />,
    href: "mailto:rahmanidrees11@gmail.com?subject=Project%20Consultation",
    description:
      "Share your product scope, existing stack, timelines, and integration requirements before development begins.",
  },
  {
    title: "Direct Contact",
    icon: <EmailIcon />,
    href: "mailto:rahmanidrees11@gmail.com",
    description:
      "Use email for enterprise SaaS, Oracle Fusion API, accounting, dashboard, or Web3 DApp project discussions.",
  },
];

const engagementExamples = [
  {
    title: "Oracle Fusion API integration",
    icon: <ApiIcon />,
    description:
      "Enterprise invoicing workflows, status sync, exception handling, and finance operations in a telecom environment.",
  },
  {
    title: "Accounting system delivery",
    icon: <ReceiptLongIcon />,
    description:
      "GL, P&L, balance sheet, invoice, approval, and reporting modules for business finance users.",
  },
  {
    title: "SaaS dashboard build",
    icon: <DashboardCustomizeIcon />,
    description:
      "Responsive admin dashboards with account management, analytics summaries, and operational workflows.",
  },
  {
    title: "Web3 wallet integration",
    icon: <AccountBalanceWalletIcon />,
    description:
      "WalletConnect, MetaMask, Ethers.js, transaction states, staking flows, and smart contract interaction.",
  },
];

function SocialProof() {
  return (
    <Box
      component="section"
      id="social-proof"
      aria-labelledby="hire-heading"
      sx={{
        py: { xs: 8, md: 12 },
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          right: "-10%",
          width: "30%",
          height: "30%",
          background: "radial-gradient(circle, rgba(29, 191, 115, 0.1) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
            animation: `${fadeInUp} 0.6s ease-out`,
          }}
        >
          <Typography
            variant="overline"
            component="p"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              letterSpacing: 3,
              mb: 1,
              display: "block",
            }}
          >
            Hire
          </Typography>
          <Typography
            id="hire-heading"
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: "linear-gradient(135deg, #ffffff 0%, #1DBF73 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Start a Project with Rahman Idrees
          </Typography>
          <Typography
            variant="body1"
            component="p"
            color="text.secondary"
            sx={{ maxWidth: 720, mx: "auto" }}
          >
            Choose the channel that fits your scope: Fiverr for defined tasks,
            Upwork for longer engagements, or direct consultation for SaaS,
            Oracle Fusion API, accounting, dashboard, and Web3 builds.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {platforms.map((platform, index) => (
            <Grid item xs={12} md={6} key={platform.name}>
              <Card
                sx={{
                  height: "100%",
                  animation: `${fadeInUp} 0.6s ease-out`,
                  animationDelay: `${index * 0.15}s`,
                  animationFillMode: "both",
                  "&:hover": {
                    borderColor: alpha(platform.color, 0.5),
                    boxShadow: `0 0 40px ${alpha(platform.color, 0.15)}`,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 58,
                        height: 58,
                        backgroundColor: alpha(platform.color, 0.16),
                        color: platform.color,
                      }}
                    >
                      {platform.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" component="h2" fontWeight={700}>
                        {platform.name}
                      </Typography>
                      <Typography variant="body2" component="p" color="text.secondary">
                        Freelance profile
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" component="p" color="text.secondary" sx={{ mb: 3 }}>
                    {platform.description}
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                    {platform.highlights.map((highlight) => (
                      <Chip
                        key={highlight}
                        label={highlight}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: alpha(platform.color, 0.45), color: platform.color }}
                      />
                    ))}
                  </Box>

                  <Button
                    component="a"
                    href={platform.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    endIcon={<LaunchIcon />}
                    aria-label={`Open ${platform.name} profile for Rahman Idrees`}
                    sx={{
                      backgroundColor: platform.color,
                      "&:hover": {
                        backgroundColor: alpha(platform.color, 0.8),
                      },
                    }}
                  >
                    View {platform.name}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" fontWeight={700} sx={{ mb: 4, textAlign: "center" }}>
            Project Consultation & Contact
          </Typography>
          <Grid container spacing={3}>
            {consultationOptions.map((option) => (
              <Grid item xs={12} md={6} key={option.title}>
                <Card sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3 }}>
                    <Avatar
                      sx={{
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        mb: 2,
                      }}
                    >
                      {option.icon}
                    </Avatar>
                    <Typography variant="h5" component="h3" fontWeight={700} sx={{ mb: 1 }}>
                      {option.title}
                    </Typography>
                    <Typography variant="body2" component="p" color="text.secondary" sx={{ mb: 3 }}>
                      {option.description}
                    </Typography>
                    <Button component="a" href={option.href} variant="outlined">
                      {option.title}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box>
          <Typography variant="h3" component="h2" fontWeight={700} sx={{ mb: 4, textAlign: "center" }}>
            Engagement Experience
          </Typography>
          <Grid container spacing={3}>
            {engagementExamples.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Card
                  sx={{
                    height: "100%",
                    animation: `${fadeInUp} 0.6s ease-out`,
                    animationDelay: `${index * 0.1}s`,
                    animationFillMode: "both",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Avatar
                      sx={{
                        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                        color: "secondary.main",
                        mb: 2,
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Typography variant="h6" component="h3" fontWeight={700} sx={{ mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" component="p" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default SocialProof;
